<template>
    <div class="page-editor">
        <page-editor-top-bar
            v-model:title="title"
            @save="sendMessage('save')"
            @back="emit('back')"
            @undo="sendMessage('undo')"
            @redo="sendMessage('redo')"
            @set-screen-mode="sendMessage('set-screen-mode', { mode: $event })"
        />

        <iframe
            ref="editorRef"
            title="Editor"
            :src="editorSource"
            referrerpolicy="no-referrer"
        />
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref, unref, watch } from 'vue';
    import { ChangePageCommand, CreateAssetCommand, Page, AssetId } from '@keyvue/one-api-client';
    import PageEditorTopBar from '@/flow/flow/components/page-editor/PageEditorTopBar.vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';

    interface Props {
        page: Page
    }

    const { client, currentCompany } = useClient();
    const { notifySaving, notifySaved, notifyError } = useNotify();
    const props = defineProps<Props>();
    const emit = defineEmits<{
        (e: 'saved', command: ChangePageCommand, close?: boolean): void;
        (e: 'back'): void;
    }>();

    const editorRef = ref<HTMLIFrameElement>();
    const title = ref(props.page.title);
    const slug = ref(props.page.slug);
    const html = ref(props.page.content !== '' ? props.page.content : '<div class="is-section"></div>');
    const mainCss = ref(props.page.mainCss);
    const sectionCss = ref(props.page.sectionCss);

    const editorSource = computed(() => {
        return [
            import.meta.env.VITE_INNOVA_EDITOR_HOST,
            import.meta.env.VITE_INNOVA_EDITOR_BASEPATH,
        ].filter((v) => !!v).join();
    });

    function sendMessage(type: string, payload: Record<string, any> = {}) {
        editorRef.value?.contentWindow?.postMessage({
            type,
            payload,
        }, editorSource.value);
    }

    async function messageHandler(event: MessageEvent) {
        if (!event.origin.startsWith(editorSource.value)) {
            return;
        }

        const { type, payload } = event.data;
        if (!type || !payload) {
            return;
        }

        // recieved once editor is initialized
        if (type === 'ready') {
            sendMessage('set-initial-values', {
                html: unref(html),
                mainCss: unref(mainCss),
                sectionCss: unref(sectionCss),
            });
        }

        // recieved once editor gets request to save
        // returns payload with current html, mainCss and sectionCss
        if (type === 'save') {
            try {
                const closeNotification = notifySaving();
                const command = new ChangePageCommand(
                    props.page.id.id,
                    title.value,
                    slug.value,
                    payload.html,
                    payload.mainCss,
                    payload.sectionCss,
                );

                await client.dispatch(command);

                closeNotification();
                notifySaved();
                emit('saved', command);

                html.value = payload.html;
                mainCss.value = payload.mainCss;
                sectionCss.value = payload.sectionCss;
            } catch (error) {
                notifyError(error);
            }
        }

        if (type === 'upload') {
            const asset = await client.dispatch(new CreateAssetCommand(
                currentCompany.value.id.toString(),
                AssetId.generate().toString(),
                payload,
            ));

            sendMessage('uploaded', {
                url: asset.url,
            });
        }
    }

    onMounted(() => {
        window.addEventListener('message', messageHandler);
    });

    onUnmounted(() => {
        window.removeEventListener('message', messageHandler);
    });

    watch(() => props.page.title, (newValue) => {
        title.value = newValue;
    });

    watch(title, (newValue) => {
        slug.value = newValue
            .normalize('NFKD') // split accented characters into their base characters and diacritical marks
            .replace(/[\u0300-\u036f]/g, '') // remove all the accents, which happen to be all in the \u03xx UNICODE block.
            .trim() // trim leading or trailing whitespace
            .toLowerCase() // convert to lowercase
            .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
            .replace(/\s+/g, '-') // replace spaces with hyphens
            .replace(/-+/g, '-'); // remove consecutive hyphens
    });

    watch(title, async (newValue) => {
        if (props.page.title === newValue) {
            return;
        }

        try {
            const command = new ChangePageCommand(
                props.page.id.id,
                title.value,
                slug.value,
                props.page.content,
                props.page.mainCss,
                props.page.sectionCss,
            );

            await client.dispatch(command);

            notifySaved('De titel van deze pagina is aangepast', '');
            emit('saved', command, false);
        } catch (error) {
            notifyError(error);
            title.value = props.page.title;
        }
    });
</script>

<style scoped lang="scss">
.page-editor {
    display: grid;
    height: 100vh;
    grid-template-rows: min-content auto;

    iframe {
        width: 100%;
        height: 100%;
        border: none;
    }
}
</style>
